import React from "react";
import { CheckIcon } from "@heroicons/react/24/outline";

export interface TimelineStepProps {
  status: string;
  role: string;
  color: string;
  completed: boolean;
  isActive: boolean;
  isPending: boolean;
  isLast?: boolean;
  size?: "xs" | "sm" | "md" | "lg";
  showLabel?: boolean;
  showConnector?: boolean;
  onClick?: () => void;
}

// Size configurations
const SIZE_CONFIG = {
  xs: {
    circle: "w-5 h-5",
    dot: "w-2 h-2",
    activeDot: "w-2.5 h-2.5",
    icon: "w-3 h-3",
    border: "border",
    activeBorder: "border-2",
    label: "text-[10px]",
    labelWidth: "w-14",
    connector: "h-[1.5px]",
    shadow: "0 0 0 2px",
    glow: "0 0 6px",
  },
  sm: {
    circle: "w-6 h-6",
    dot: "w-2 h-2",
    activeDot: "w-3 h-3",
    icon: "w-3.5 h-3.5",
    border: "border",
    activeBorder: "border-2",
    label: "text-xs",
    labelWidth: "w-16",
    connector: "h-[1.5px]",
    shadow: "0 0 0 2px",
    glow: "0 0 6px",
  },
  md: {
    circle: "w-8 h-8",
    dot: "w-2.5 h-2.5",
    activeDot: "w-4 h-4",
    icon: "w-5 h-5",
    border: "border-2",
    activeBorder: "border-[3px]",
    label: "text-xs",
    labelWidth: "w-20",
    connector: "h-[2px]",
    shadow: "0 0 0 4px",
    glow: "0 0 12px",
  },
  lg: {
    circle: "w-10 h-10",
    dot: "w-3 h-3",
    activeDot: "w-5 h-5",
    icon: "w-6 h-6",
    border: "border-2",
    activeBorder: "border-[3px]",
    label: "text-sm",
    labelWidth: "w-24",
    connector: "h-[2px]",
    shadow: "0 0 0 4px",
    glow: "0 0 12px",
  },
};

export const TimelineStep: React.FC<TimelineStepProps> = ({
  status,
  color,
  completed,
  isActive,
  isPending,
  isLast = false,
  size = "md",
  showLabel = true,
  showConnector = true,
  onClick,
}) => {
  const config = SIZE_CONFIG[size];
  const formattedStatus = status?.replaceAll("_", " ") || "";

  return (
    <div className="relative flex-1">
      <div className="flex items-center">
        <div className="flex flex-col items-center">
          {/* Circle with icon/dot */}
          <div
            className={`z-10 flex items-center justify-center ${
              config.circle
            } rounded-full bg-white transition-all duration-300 ${
              isActive ? config.activeBorder : config.border
            } ${onClick ? "cursor-pointer hover:scale-110" : ""}`}
            style={{
              borderColor: color,
              boxShadow: isActive
                ? `${config.shadow} ${color}25, ${config.glow} ${color}40`
                : undefined,
            }}
            onClick={onClick}
          >
            {/* Completed - show checkmark */}
            {completed && !isActive ? (
              <CheckIcon
                className={`${config.icon} transition-all duration-300`}
                style={{
                  color: color,
                  strokeWidth: 2.5,
                }}
              />
            ) : isActive ? (
              /* Active - show pulsing dot */
              <div
                className={`${config.activeDot} rounded-full animate-pulse`}
                style={{
                  backgroundColor: color,
                  boxShadow: `0 0 8px ${color}60`,
                }}
              />
            ) : null}
          </div>

          {/* Label */}
          {showLabel && (
            <div className="h-[40px] flex items-start justify-center">
              <span
                className={`mt-2 sm:mt-3 ${
                  config.label
                } text-center whitespace-normal ${
                  config.labelWidth
                } transition-all duration-300 ${
                  isActive
                    ? "text-gray-900 font-bold"
                    : completed
                    ? "text-gray-700 font-semibold"
                    : "text-gray-400 font-medium"
                }`}
              >
                {formattedStatus}
              </span>
            </div>
          )}
        </div>

        {/* Connector line */}
        {!isLast && showConnector && (
          <div
            className={`flex-1 ${config.connector} mx-1 sm:mx-2 ${
              showLabel ? "mt-4" : ""
            } transition-all duration-300`}
            style={{
              backgroundColor: completed || isActive ? color : "#E5E7EB",
            }}
          />
        )}
      </div>
    </div>
  );
};